import React, { useState, useEffect } from 'react';
import { userService } from '../services/api';
import { getUserImageUrl } from '../utils/imageHelper';
import '../styles/avatar.scss';

interface AvatarUploadProps {
  userId: number;
  isSpouseImage?: boolean;
  currentImageUrl?: string;
  onUploadSuccess: (imageUrl: string) => void;
  onError: (error: string) => void;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  userId,
  isSpouseImage,
  currentImageUrl,
  onUploadSuccess,
  onError,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(
    getUserImageUrl(currentImageUrl)
  );

  useEffect(() => {
    setPreviewUrl(getUserImageUrl(currentImageUrl));
  }, [currentImageUrl]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      onError('Chỉ hỗ trợ tệp JPG, PNG hoặc GIF');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      onError('Kích thước tệp không được vượt quá 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    setIsUploading(true);
    try {
      const response = await userService.uploadImage(userId, file, isSpouseImage ?? false);
      if (response.success) {
        const imageUrl = isSpouseImage ? response.data.spouseImageUrl : response.data.imageUrl;
        setPreviewUrl(getUserImageUrl(imageUrl));
        onUploadSuccess(imageUrl);
      } else {
        onError(response.message || 'Lỗi khi tải lên ảnh đại diện');
      }
    } catch (err: any) {
      onError(err.response?.data?.message || 'Lỗi khi tải lên ảnh đại diện');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="avatar-upload">
      <div className="avatar-preview">
        {previewUrl ? (
          <img src={previewUrl} alt="Avatar preview" className="avatar-image" />
        ) : (
          <div className="avatar-placeholder">
            <i className="fas fa-user"></i>
          </div>
        )}
      </div>

      <label className="avatar-upload-label">
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.gif"
          onChange={handleFileSelect}
          disabled={isUploading}
          style={{ display: 'none' }}
        />
        <span className="btn btn-sm btn-primary" style={{ cursor: 'pointer' }}>
          {isUploading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i> Đang tải...
            </>
          ) : (
            <>
              <i className="fas fa-camera"></i> Thay đổi ảnh đại diện
            </>
          )}
        </span>
      </label>
    </div>
  );
};
